import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock, Loader2, CalendarDays, CheckCircle2, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHead, EASE } from "@/lib/motion";
import { fetchConsultationTypes, fetchAvailability, createBooking, formatApiError } from "@/lib/api";

const money = (v) => `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 0 })}`;
const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtSlot = (iso, tz) => new Date(iso).toLocaleTimeString("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit" });

export default function BookConsultation() {
  const [params] = useSearchParams();
  const [types, setTypes] = useState(null);
  const [type, setType] = useState(null);
  const [day, setDay] = useState(todayISO());
  const [avail, setAvail] = useState(null);
  const [slot, setSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    fetchConsultationTypes().then(setTypes).catch(() => setTypes([]));
    if (params.get("cancelled")) toast.message("Checkout was cancelled — your time slot was released.");
  }, []);

  useEffect(() => {
    if (!type) return;
    setSlot(null);
    setLoadingSlots(true);
    fetchAvailability(type.id, day)
      .then(setAvail)
      .catch(() => setAvail({ slots: [], timezone: "" }))
      .finally(() => setLoadingSlots(false));
  }, [type, day]);

  const maxDay = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 60);
    return d.toISOString().slice(0, 10);
  }, []);

  const submit = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) return toast.error("Enter your full name.");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return toast.error("Enter a valid email.");
    setSubmitting(true);
    try {
      const res = await createBooking({
        type_id: type.id, name: form.name.trim(), email: form.email.trim(),
        phone: form.phone.trim(), notes: form.notes.trim(), start: slot,
        origin_url: window.location.origin,
      });
      if (res.checkout_url) { window.location.href = res.checkout_url; return; }
      setDone(res);
    } catch (e) {
      toast.error(formatApiError(e));
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-xl px-6 py-32 text-center" data-testid="book-free-confirmed">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
        <h1 className="mt-6 font-display text-3xl font-medium text-steel-text">You're booked</h1>
        <p className="mt-3 text-steel-secondary">Check your email for the confirmation and meeting details.</p>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="book-page">
      <section className="mx-auto max-w-5xl px-6 py-16 lg:px-10 lg:py-24">
        <SectionHead overline="Book a consultation" title="Reserve time with Jared."
          copy="Pick the consultation that fits, choose a time, and secure it with a booking deposit." />
        <div className="mt-6 inline-flex items-center gap-2.5 border border-steel-brass/40 bg-steel-brass/5 px-4 py-2.5" data-testid="deposit-policy-badge">
          <ShieldCheck className="h-4 w-4 shrink-0 text-steel-brass" />
          <p className="text-sm text-steel-secondary">
            Your <strong className="font-semibold text-steel-text">deposit</strong> is{" "}
            <strong className="font-semibold text-steel-brass">non-refundable</strong> — but it becomes account credit toward any Steele Industries bill.
          </p>
        </div>
        <p className="mt-4 text-sm text-steel-muted">
          Just have a quick question?{" "}
          <Link to="/contact" data-testid="book-message-link" className="font-medium text-steel-brass underline-offset-2 hover:underline">Send a message instead →</Link>
        </p>

        {/* STEP 1 — type */}
        <div className="mt-12">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-steel-brass">1 · Choose a consultation</p>
          {!types && <div className="mt-6"><Loader2 className="h-6 w-6 animate-spin text-steel-brass" /></div>}
          {types && types.length === 0 && (
            <p className="mt-6 text-sm text-steel-secondary" data-testid="book-no-types">No consultation types are open for booking right now. Please check back soon.</p>
          )}
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {types?.map((t) => (
              <button key={t.id} data-testid={`book-type-${t.id}`} onClick={() => setType(t)}
                className={`group flex flex-col border p-6 text-left transition-colors duration-300 ${type?.id === t.id ? "border-steel-brass bg-steel-surface shadow-[0_0_28px_rgba(168,85,247,0.14)]" : "border-steel-border bg-steel-surface/60 hover:border-steel-muted"}`}>
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-xl font-medium text-steel-text">{t.name}</h3>
                  <span className="shrink-0 font-mono text-sm text-steel-brass">{t.deposit_amount > 0 ? `${money(t.deposit_amount)} deposit` : "Free"}</span>
                </div>
                {t.description && <p className="mt-2 text-sm text-steel-secondary">{t.description}</p>}
                <span className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">
                  <Clock className="h-3.5 w-3.5" /> {t.duration_minutes} min
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* STEP 2 — time */}
        {type && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="mt-14">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-steel-brass">2 · Pick a time</p>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-3 border border-steel-border bg-steel-surface px-4 py-3">
                <CalendarDays className="h-4 w-4 text-steel-brass" />
                <input type="date" data-testid="book-date" value={day} min={todayISO()} max={maxDay}
                  onChange={(e) => setDay(e.target.value)}
                  className="bg-transparent text-sm text-steel-text outline-none [color-scheme:dark]" />
              </label>
              {avail?.timezone && <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">Times shown in {avail.timezone.replace("_", " ")}</span>}
            </div>
            <div className="mt-6 min-h-[48px]">
              {loadingSlots ? (
                <Loader2 className="h-5 w-5 animate-spin text-steel-brass" />
              ) : avail?.slots?.length ? (
                <div className="flex flex-wrap gap-2" data-testid="book-slots">
                  {avail.slots.map((s) => (
                    <button key={s} data-testid={`book-slot-${s}`} onClick={() => setSlot(s)}
                      className={`border px-4 py-2 text-sm transition-colors duration-200 ${slot === s ? "border-steel-brass bg-steel-brass/10 text-steel-brass" : "border-steel-border text-steel-secondary hover:border-steel-muted hover:text-steel-text"}`}>
                      {fmtSlot(s, avail.timezone)}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-steel-muted" data-testid="book-no-slots">No open times on this day — try another date.</p>
              )}
            </div>
          </motion.div>
        )}

        {/* STEP 3 — details */}
        {type && slot && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }} className="mt-14">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-steel-brass">3 · Your details</p>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input data-testid="book-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="border border-steel-border bg-steel-surface px-4 py-3 text-sm text-steel-text outline-none focus:border-steel-brass" />
              <input data-testid="book-email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="border border-steel-border bg-steel-surface px-4 py-3 text-sm text-steel-text outline-none focus:border-steel-brass" />
              <input data-testid="book-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="border border-steel-border bg-steel-surface px-4 py-3 text-sm text-steel-text outline-none focus:border-steel-brass" />
              <input data-testid="book-notes" placeholder="What would you like to cover? (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="border border-steel-border bg-steel-surface px-4 py-3 text-sm text-steel-text outline-none focus:border-steel-brass sm:col-span-2" />
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4 border border-steel-border bg-steel-surface/60 p-5">
              <div className="min-w-0">
                <p className="text-sm text-steel-text"><strong>{type.name}</strong> · {fmtSlot(slot, avail.timezone)} on {new Date(slot).toLocaleDateString("en-US", { timeZone: avail.timezone, weekday: "long", month: "long", day: "numeric" })}</p>
                <p className="mt-1 text-xs text-steel-secondary">{type.deposit_amount > 0 ? (
                  <><strong className="font-semibold text-steel-text">{money(type.deposit_amount)} deposit</strong> · <strong className="font-semibold text-steel-brass">non-refundable</strong> — becomes account credit toward any bill.</>
                ) : "No deposit required."}</p>
              </div>
              <button data-testid="book-submit" onClick={submit} disabled={submitting}
                className="ml-auto flex items-center gap-2 bg-steel-accent px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-steel-accentHover disabled:opacity-50">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
                {type.deposit_amount > 0 ? `Pay ${money(type.deposit_amount)} & book` : "Confirm booking"}
              </button>
            </div>
          </motion.div>
        )}
      </section>
    </div>
  );
}
